// src/components/VideoSettings.js
import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';
import api from '../api/api';
import { useToast } from './Toasts';

import VideoSelect from './VideoSelect';
import { useVideos } from '../hooks/useVideos';

import GameSelect from './GameSelect'; 

function VideoSettings({ darkMode, cardBgClass }) {
  const { notify } = useToast();
  const [mode, setMode] = useState('random');
  const [minNoRepeat, setMinNoRepeat] = useState(5);
  const [manualOrder, setManualOrder] = useState([]);
  const [saving, setSaving] = useState(false);

  const { videos: allVideos, refresh } = useVideos();
  const fetchVideos = refresh;  // Alias für Abwärtskompatibilität
  
  useEffect(() => {
    fetchSettings();
    refresh(); 
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      if (res.data) {
        setMode(res.data.mode || 'random');
        setMinNoRepeat(res.data.minNoRepeat || 5);
        setManualOrder(res.data.manualOrder || []);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Einstellungen:', err);
    }
  };

  const handleSave = async () => {
    const settings = {
      mode,
      minNoRepeat,
      manualOrder: mode === 'manual' ? manualOrder : [],
    };

    setSaving(true);
    try {
      await api.post('/settings', settings);
      notify('Einstellungen gespeichert.');
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      notify('Einstellungen konnten nicht gespeichert werden.', 'danger');
    }
    setSaving(false);
  };

  const handleManualOrderChange = (index, value) => {
    const updated = [...manualOrder];
    updated[index] = value;
    setManualOrder(updated);
  };

  const addToManualOrder = () => {
    setManualOrder([...manualOrder, '']);
  };

  const removeFromManualOrder = (index) => {
    const updated = [...manualOrder];
    updated.splice(index, 1);
    setManualOrder(updated);
  };

  return (
    <Card className={`p-4 shadow-sm mb-5 ${darkMode ? cardBgClass : ''}`}>
      <h4 className="mb-4">⚙️ Wiedergabe-Einstellungen</h4>

      <Form.Group className="mb-3">
        <Form.Label className={darkMode ? 'text-light' : ''}>Wiedergabemodus:</Form.Label>
        <Form.Select
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className={darkMode ? cardBgClass : ''}
        >
          <option value="random">🎲 Zufällig (mit Wiederholsperre)</option>
          <option value="sequential">📂 Sequenziell (Liste von vorne)</option>
          <option value="randomized-sequential">📂 Sequenziell (Liste Random)</option>
          <option value="manual">✍️ Manuelle Reihenfolge</option>
          <option value="manual-random">✍️ Manuelle Reihenfolge (Random)</option>
        </Form.Select>
      </Form.Group>

      {mode === 'random' && (
        <Form.Group className="mb-3">
          <Form.Label className={darkMode ? 'text-light' : ''}>
            Mind. Anzahl Videos ohne Wiederholung:
          </Form.Label>
          <Form.Control
            type="number"
            min="1"
            value={minNoRepeat}
            onChange={(e) => setMinNoRepeat(Number(e.target.value))}
            className={darkMode ? `${cardBgClass} border-light` : ''}
          />
        </Form.Group>
      )}

      {mode === 'manual' && (
        <>
          <Form.Label className={darkMode ? 'text-light' : ''}>Manuelle Reihenfolge:</Form.Label>
          {manualOrder.map((video, idx) => (
            <Row key={idx} className="mb-2">
              <Col>
                <VideoSelect
                  videos={allVideos}
                  value={video}
                  onChange={(val) => handleManualOrderChange(idx, val)}
                  className={darkMode ? cardBgClass : ''}
                  darkMode={darkMode}
                  placeholder="-- Video auswählen --"
                />
              </Col>
              <Col xs="auto">
                <Button variant="danger" onClick={() => removeFromManualOrder(idx)}>🗑️</Button>
              </Col>
            </Row>
          ))}
          <Button variant="info" onClick={addToManualOrder}>➕ Video hinzufügen</Button>
        </>
      )}

      <hr />

      <Button variant="primary" onClick={handleSave} disabled={saving}>
        {saving ? '💾 Speichern...' : '💾 Einstellungen speichern'}
      </Button>
    </Card>
  );
}

export default VideoSettings;